import { localNotificationStorage, LocalNotificationStorage } from '@/services/local-notification/local-notification-storage'
import { BaseError } from '@/util/base-error'
import { ApiClient } from '@ajax/modules/services/client'
import { Either, Left, Right } from 'fp-ts/lib/Either'

const HTTP_STATUS_UNAUTHORIZED = 401
const REGISTERED_AUTH_INVALIDATED_ERROR_CODE = 'registered_auth_invalidated'
const AUTH_INVALIDATED_NOTIFICATION_ID = 'auth-invalidated'
const AUTH_VALIDATE_ENDPOINT = '/api/auth/validate'

export class InvalidAuthenticationStateError extends BaseError {}

interface ValidateAuthApiClient {
  get(endpoint: string): PromiseLike<unknown>
}

type LocalNotificationWritableStorage = Pick<LocalNotificationStorage, 'add'>

const isObject = (value: unknown): value is { [key: string]: unknown } =>
  typeof value === 'object' && value !== null

const authValidateErrorCode = (error: unknown): string | null => {
  if (!isObject(error) || !isObject(error.response) || !isObject(error.response.body)) {
    return null
  }

  return typeof error.response.body.error_code === 'string'
    ? error.response.body.error_code
    : null
}

export class ValidateAuthService {
  static createDefault(): ValidateAuthService {
    return new ValidateAuthService(ApiClient, localNotificationStorage)
  }

  constructor(
    private readonly apiClient: ValidateAuthApiClient,
    private readonly notificationStorage: LocalNotificationWritableStorage
  ) {}

  async validate(): Promise<Either<Error, void>> {
    try {
      await this.apiClient.get(AUTH_VALIDATE_ENDPOINT)

      return new Right(undefined)
    } catch (error) {
      if (this.isUnauthorized(error)) {
        this.addAuthInvalidatedNotificationIfNeeded(error)

        return new Left(new InvalidAuthenticationStateError('auth validation failed'))
      }

      return new Left(error instanceof Error ? error : new Error('fail to validate auth'))
    }
  }

  private isUnauthorized(error: unknown): boolean {
    return isObject(error) && error.status === HTTP_STATUS_UNAUTHORIZED
  }

  private addAuthInvalidatedNotificationIfNeeded(error: unknown): void {
    if (authValidateErrorCode(error) !== REGISTERED_AUTH_INVALIDATED_ERROR_CODE) {
      return
    }

    this.notificationStorage.add({
      id: AUTH_INVALIDATED_NOTIFICATION_ID,
      kind: 'auth-invalidated',
      title: 'ログアウトしました。再度ログインしてください。',
      button: {
        label: 'ログインする',
        path: '/s/login',
      },
    })
  }
}
